/** @jsx jsx */
import { jsx } from '@emotion/core';

import React, { Fragment } from 'react';
import { RouteComponentProps, navigate } from '@reach/router';
import { ProceedOrCancelButton } from '../../../../pge-plus-common/StepperForm';
import { Box, Divider, FormHelperText, Grid } from '@material-ui/core';
import { GridItem } from '../../../../pge-plus-common/Grid';
import { Question } from '../../../../pge-plus-common/Typography';
import useFormState from '../../../../../hooks/useFormState';
import { connectVehicleToAppValidateFunction } from '../rebateOnlyForm.rules';
import {
  getConfirmationEvRebate,
  getPreRebateEligibilityConfirmationState,
  handleUnableToConnectAppError,
  navigateToIneligibleFiddlesticksVehicleFlow,
} from '../rebateOnlyForm.utils';
import useContentMgmt from '../../../../../hooks/useContentMgmt';
import { RebateQualification } from '../rebateOnlyForm.types';
import microcopyGroupIds from '../../../../pge-plus-common/microcopyGroupIds';
import RadioButtonGroup from '../../../../pge-plus-common/RadioButtonGroup';
import { SaveAndComeBackLink } from '../../../../pge-plus-common/SaveAndComeBackLink';
import routes from '../../../../../routes';
import { useRebateOnlyFormState } from '../../../../../providers/RebateOnlyFormStateProvider';
import {
  ApplicationErSectionType,
  SectionErRebateConfirm,
} from '../../../../../__generated__/pgeplus-types';
import useUpdateERSections from '../../../../../hooks/pge-plus/useUpdateERSections';
import useWrapWithLoader from '../../../../../hooks/useWrapWithLoading';
import { rebateConfirmERFormStateAdapter } from '../rebateOnlyFormState.adapter';
import useUtil from '../../../../need-more-time-to-pay/useUtil';
import colors from '../../../../../themes/main-colors';

const ConnectVehicleToApp: React.FC<RouteComponentProps> = (
  props: RouteComponentProps,
) => {
  const { content } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_REBATE_ONLY_CHARGER_CONNECT_TO_WIFI,
  );
  const { content: contentMCG } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_EV_REBATES_FLOW_MCG,
  );
  const {
    formState: { rebateConfirm, rebateInfo, application },
    getSectionData,
    updateFormState,
  } = useRebateOnlyFormState();

  const form = useFormState(
    rebateConfirm.rebateQualification!, //TODO: This should be taken from the global app state
    {
      validate: connectVehicleToAppValidateFunction(),
      validationContext: {
        content,
      },
    },
  );

  const question = contentMCG.get(
    'PGE_PLUS_EV_REBATES_FLOW_REBATE_ELIGIBILITY_CONNECT_CHARGER_QUESTION_SECONDARY_BOTH_QUALIFIED',
  );

  const { wrapWithLoader } = useWrapWithLoader();

  const sectionDataRebateConfirm: SectionErRebateConfirm = getSectionData(
    ApplicationErSectionType.ErRebateConfirm,
  );
  const { updateSectionRebateConfirm } = useUpdateERSections();
  const { setErrorNotification } = useUtil();

  const onSubmit = wrapWithLoader(async (data: RebateQualification) => {
    try {
      const response = await updateSectionRebateConfirm(
        rebateConfirmERFormStateAdapter().fromFormState(
          {
            ...rebateConfirm,
            // CUP-24446: Clears the legacy Tesla flag so isLegacyTeslaResumeCustomer() stops misrouting Back on later screens once this is answered
            connectTeslaToEVPulse: undefined,
            rebateQualification: {
              ...rebateConfirm.rebateQualification,
              willConnectVehicleManufacturerApp: data.willConnectVehicleManufacturerApp,
            },
            chargerInstallInfo: {
              ...rebateConfirm.chargerInstallInfo,
              installationCost:
                rebateConfirm.chargerInstallInfo?.isChargerPurchased === false
                  ? undefined
                  : rebateConfirm.chargerInstallInfo?.installationCost,
              chargerCost:
                rebateConfirm.chargerInstallInfo?.isChargerPurchased === false
                  ? undefined
                  : rebateConfirm.chargerInstallInfo?.chargerCost,
              receiptImages:
                rebateConfirm.chargerInstallInfo?.isChargerPurchased === false
                  ? undefined
                  : rebateConfirm.chargerInstallInfo?.receiptImages,
            },
          },
          application?.id!,
          sectionDataRebateConfirm?.id,
        ),
      );

      const result = response?.data?.updateSectionERRebateConfirm;

      if (response?.errors || result?.validation?.errors) {
        throw new Error();
      }

      if (result?.section?.data) {
        const updatedValues = rebateConfirmERFormStateAdapter().toFormState(
          result.section.data,
        );
        updateFormState('rebateConfirm', updatedValues);
      }

      if (
        result?.section.errors.length &&
        handleUnableToConnectAppError(result?.section.errors)
      ) {
        return;
      }

      if (!result?.section.errors.length) {
        // CUP-24446: No sends the customer to the Fiddlesticks ineligible page, Yes takes them to "Let's Connect"
        if (data.willConnectVehicleManufacturerApp === false) {
          navigateToIneligibleFiddlesticksVehicleFlow(
            rebateInfo.smartCharging?.vehicleManufacturer,
          );
        } else {
          void navigate(routes.PGE_PLUS_REBATE_ONLY_VEHICLE_INSTRUCTIONS);
        }
      } else {
        throw new Error();
      }
    } catch (e) {
      setErrorNotification(true);
    }
  });

  const handleSaveAndExit = wrapWithLoader(async () => {
    try {
      const response = await updateSectionRebateConfirm({
        ...rebateConfirmERFormStateAdapter().fromFormState(
          {
            ...rebateConfirm,
            connectTeslaToEVPulse: undefined,
            rebateQualification: {
              ...rebateConfirm.rebateQualification,
              willConnectVehicleManufacturerApp:
                form.values?.willConnectVehicleManufacturerApp,
            },
          },
          application?.id!,
          sectionDataRebateConfirm?.id,
        ),
        saveAndComeBack: true,
      });
      if (
        response?.errors ||
        response.data?.updateSectionERRebateConfirm?.validation?.errors ||
        response.data?.updateSectionERRebateConfirm?.section?.errors.length !==
          0
      ) {
        throw new Error();
      } else {
        void navigate(routes.ACCOUNT);
      }
    } catch {
      setErrorNotification(true);
    }
  });

  return (
    <Fragment>
      <form
        onSubmit={form.submit(values => onSubmit(values), console.log)}
        noValidate
      >
        <Grid
          container
          css={{
            rowGap: 24,
          }}
        >
          <GridItem>
            <Question
              title={question}
              popoverContent={content.get('WIFI_LEARN_MORE_TOOLTIP')}
              popoverLinkContent={content.get('REUSABLE_LEARN_MORE')}
            />
          </GridItem>
          <GridItem>
            <Box
              css={{
                width: 'max-content',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            >
              <RadioButtonGroup
                options={[
                  {
                    label: content.get('REUSABLE_YES') || '',
                    value: true,
                  },
                  {
                    label: content.get('REUSABLE_NO') || '',
                    value: false,
                  },
                ]}
                state={form.values.willConnectVehicleManufacturerApp}
                setState={e => form.setValue('willConnectVehicleManufacturerApp', e)}
              />
              {form.errors.willConnectVehicleManufacturerApp && (
                <FormHelperText error>
                  {form.errors.willConnectVehicleManufacturerApp}
                </FormHelperText>
              )}
            </Box>
          </GridItem>
          <GridItem>
            <Divider css={{ color: colors.gray93, margin: '20px 0 0px 0' }} />
            <ProceedOrCancelButton
              back={routes.PGE_PLUS_REBATE_ONLY_ELIGIBILITY_CONFIRMATION}
              backRouteState={{
                ...getPreRebateEligibilityConfirmationState(
                  getConfirmationEvRebate(
                    rebateConfirm.preRebateEligibility2,
                    rebateConfirm.preRebateEligibility1,
                    rebateConfirm.chargerInstallInfo?.isChargerPurchased,
                  ),
                  false,
                  true,
                  rebateConfirm.rebateInfo?.existingEnrollments?.iqbd,
                  rebateConfirm.iqbd?.checkEligibility,
                ),
                nextButtonRoute: routes.PGE_PLUS_REBATE_ONLY_CONNECT_VEHICLE,
              }}
              disable={
                form.values?.willConnectVehicleManufacturerApp === undefined ||
                form.values?.willConnectVehicleManufacturerApp === null
              }
            />
          </GridItem>
        </Grid>
        <SaveAndComeBackLink
          content={content}
          onSaveAndExit={handleSaveAndExit}
        />
      </form>
    </Fragment>
  );
};

export default ConnectVehicleToApp;
